import * as React from 'react';
import Preloader from './nv-chart/partials/Preloader';
import TypesenseProvider from './nv-chart/partials/typesense/Provider';
import OpenSearchProvider from './nv-chart/partials/opensearch/Provider';
import HiddenDataTable from './nv-chart/partials/HiddenDataTable';

// The group is consumed from an incoming (Share) URL on load, then stripped
// with the other permalink params so the address bar stays clean during use
// (§1.4). It is NOT written back live on group change — it's pinned only when
// the user clicks Share (buildShareUrl encodes it).
const getInitialGroupFromUrl = (groups) => {
  const urlParams = new URLSearchParams(window.location.search);
  const requestedGroup = urlParams.get('group');
  const matchedGroup = groups.find(group => group.label.toLowerCase() === requestedGroup?.toLowerCase());
  const defaultGroup = groups[0];

  return matchedGroup?.label ?? defaultGroup?.label;
};

export default function NvChart(props) {
  const {settings} = props;
  const {title, style, lineStyle, provider, collapsibleFilters, imageFooter, groups = []} = settings;

  // Preview token / index are OpenSearch-only — Typesense has no preview collection.
  const previewIndex = React.useMemo(
    () => (provider === 'opensearch'
      ? document.getElementById('library-preview-token')?.dataset?.index
      : null),
    [provider]
  );
  const resolvedIndex = previewIndex ?? settings.defaultIndex;

  const [groupLabel, setGroupLabel] = React.useState(() => getInitialGroupFromUrl(groups));

  const handleGroupChange = React.useCallback((newLabel) => {
    setGroupLabel(newLabel);
  }, []);

  const currentGroup = React.useMemo(
    () => groups.find(g => g.label === groupLabel),
    [groups, groupLabel]
  );

  if (groups.length === 0 || !currentGroup) {
    return <Preloader title='Chart'/>;
  }

  const providerProps = {
    defaultIndex: resolvedIndex,
    // The Table view reads the injected data script keyed by the non-preview
    // index — same source as HiddenDataTable — so keep it distinct from the
    // (possibly preview) resolvedIndex used for the live search.
    collectionName: settings.defaultIndex,
    currentGroup,
    style,
    lineStyle,
    title,
    collapsibleFilters: !!collapsibleFilters,
    // Chart-wide footer for downloaded images ({ source, disclaimer }).
    imageFooter,
    groups,
    groupLabel,
    handleGroupChange,
  };

  return (
    <div className='nv-chart-groups'>
      {provider === 'opensearch'
        ? <OpenSearchProvider {...providerProps}/>
        : <TypesenseProvider {...providerProps}/>
      }
      <HiddenDataTable collectionName={settings.defaultIndex}/>
    </div>
  );
}
